import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import LoadingSpinner from '../common/LoadingSpinner';
import type { ContractSummary } from '../../services/types';
import { formatGermanDate } from '../../utils/dateFormat';
import { describeContractStatus } from './contractStatus';

/** Props der ContractList-Komponente. */
export interface ContractListProps {
  /** Vertraege des gewaehlten Benutzers oder null (laedt noch/Fehler). */
  readonly contracts: readonly ContractSummary[] | null;
  /** true, waehrend die Vertraege geladen werden. */
  readonly loading: boolean;
  /** Deutsche Fehlermeldung oder null. */
  readonly error: string | null;
  /** Meldet den Wunsch, das PDF eines Vertrags anzuzeigen. */
  readonly onShowPdf: (contract: ContractSummary) => void;
}

/**
 * Mietvertraege des gewaehlten Benutzers (unter den Stammdaten).
 *
 * Der Button ist immer aktiv: Fuer finalisierte Vertraege zeigt er das
 * fertige DocStruct-PDF, fuer Entwuerfe eine live erzeugte Vorschau (siehe
 * contractService.fetchContractPdf). Welcher der beiden Endpunkte
 * angesprochen wird, entscheidet die Elternkomponente anhand von
 * `contract.pdfAvailable`; ContractList muss das nicht wissen.
 */
function ContractList({
  contracts,
  loading,
  error,
  onShowPdf,
}: ContractListProps): React.JSX.Element {
  const rows: readonly ContractSummary[] = contracts ?? [];

  return (
    <Card id="contract-list-card" className="shadow-sm">
      <Card.Body id="contract-list-card-body">
        <Card.Title as="h2" id="contract-list-title" className="h6 mb-3">
          Mietverträge
        </Card.Title>

        {loading && (
          <div id="contract-list-loading-container" className="d-flex justify-content-center py-4">
            <LoadingSpinner id="contract-list-loading" />
          </div>
        )}

        {!loading && error !== null && (
          <Alert id="contract-list-error" variant="danger" className="mb-0">
            {error}
          </Alert>
        )}

        {!loading && error === null && rows.length === 0 && (
          <p id="contract-list-empty" className="text-muted mb-0">
            Keine Mietverträge vorhanden.
          </p>
        )}

        {!loading && error === null && rows.length > 0 && (
          <div id="contract-list-table-wrapper" className="table-responsive">
            <Table id="contract-list" size="sm" className="align-middle mb-0">
              <thead id="contract-list-head">
                <tr id="contract-list-head-row">
                  <th id="contract-list-header-number" scope="col">
                    Vertragsnummer
                  </th>
                  <th id="contract-list-header-status" scope="col">
                    Status
                  </th>
                  <th id="contract-list-header-created" scope="col" className="text-nowrap">
                    Erstellt am
                  </th>
                  <th id="contract-list-header-actions" scope="col" className="text-end">
                    Aktion
                  </th>
                </tr>
              </thead>
              <tbody id="contract-list-body-rows">
                {rows.map((contract) => {
                  const status = describeContractStatus(contract.status);
                  return (
                    <tr key={contract.id} id={`contract-row-${contract.id}`}>
                      <td id={`contract-row-${contract.id}-number`} className="text-break">
                        {contract.contractNumber ?? '–'}
                      </td>
                      <td id={`contract-row-${contract.id}-status`}>
                        <Badge
                          id={`contract-row-${contract.id}-status-badge`}
                          bg={status.variant}
                          text={status.darkText ? 'dark' : undefined}
                        >
                          {status.label}
                        </Badge>
                      </td>
                      <td id={`contract-row-${contract.id}-created`} className="text-nowrap">
                        {formatGermanDate(contract.createdAt)}
                      </td>
                      <td id={`contract-row-${contract.id}-actions`} className="text-end">
                        {/*
                          Bewusst ohne Icon und ohne text-nowrap: In der
                          schmalen Detailspalte (lg=5) darf die Beschriftung
                          umbrechen, statt die Tabelle horizontal scrollen
                          zu lassen.
                        */}
                        <Button
                          id={`contract-pdf-button-${contract.id}`}
                          variant="primary"
                          size="sm"
                          title={
                            contract.pdfAvailable
                              ? 'PDF anzeigen'
                              : 'Vorschau anzeigen (Vertrag noch nicht finalisiert)'
                          }
                          onClick={() => onShowPdf(contract)}
                        >
                          PDF anzeigen
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default ContractList;
